import { useReactiveVar } from "@apollo/client";
import { Href, useRouter } from "expo-router";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Alert, Platform } from "react-native";

import RecommendationPlayerView from "./RecommendationPlayerView";

import { CustomPromptRefProps } from "@/components/molecules/CustomPrompt/CustomPromptContainer";
import {
  Position,
  RecommendationsPlayerRecommendationResultFragment,
  useRecommendationsRemovePlayerFromRecommendationsMutation,
} from "@/generated/graphql";
import useCreateWatchlistActions from "@/hooks/useCreateWatchlistActions";
import useExpandedPlayerUrl from "@/hooks/useExpandedPlayerUrl";
import useMobileScreen from "@/hooks/useMobileScreen";
import useRecordUserGoal, { GoalType } from "@/hooks/useRecordUserGoal";
import { useToastNotification } from "@/hooks/useToastNotification";
import { useNavigationContext } from "@/providers/NavigationProvider";
import { useTheme } from "@/providers/ThemeProvider";
import { playerDetailVar } from "@/stores/player";
import { recommendationsCategorySelectedVar } from "@/stores/recommendationsCategorySelection";
import { getPositionAndTeamName } from "@/utils/getPlayerInformations";
/**
 * Props interface for RecommendationPlayer container component
 */
interface Props {
  data: RecommendationsPlayerRecommendationResultFragment;
}

/**
 * Container component for recommendation player item with swipe-to-delete
 * @remarks
 * Manages player removal, watchlist actions, navigation, and swipeable interactions.
 * Uses platform-specific deletion confirmation: window.confirm on web, Alert.alert on native.
 * @param root0 - Component props
 * @param root0.data - Player recommendation result fragment with player data and rank change
 */
const RecommendationPlayerContainer = ({ data }: Props) => {
  const promptRef = useRef<CustomPromptRefProps>(
    null
  ) as React.RefObject<CustomPromptRefProps>;
  const router = useRouter();
  const { pushBreadcrumb, clearBreadcrumbs } = useNavigationContext();
  const playerUrl = useExpandedPlayerUrl(`/players/${data?.player?.id ?? ""}`);
  const toast = useToastNotification();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const recommendationsCategorySelected = useReactiveVar(
    recommendationsCategorySelectedVar
  );
  const isSmallScreen = useMobileScreen();
  const { handleAddWatchlistPress, loading } = useCreateWatchlistActions({
    promptRefForModal: promptRef,
  });
  const recordRemovePlayerFromRecommendationsGoal = useRecordUserGoal({
    goalType: GoalType.RemovePlayerFromRecommendations,
    value: data?.player?.id ?? "",
  });
  const [removePlayerFromRecommendations, { loading: isDeleting }] =
    useRecommendationsRemovePlayerFromRecommendationsMutation();
  const [showActionSheet, setShowActionSheet] = useState<boolean>(false);

  const playerName = useMemo(() => data?.player?.name ?? "", [data?.player]);
  const positionAndTeamName = useMemo(
    () =>
      getPositionAndTeamName(
        data?.player?.positions as Position[],
        data?.player?.team?.name ?? data?.player?.teamName ?? undefined,
        isDeleting
      ),
    [data.player, isDeleting]
  );
  const rankChange = useMemo(() => data?.rankChange ?? 0, [data.rankChange]);

  const isPlayerInAnyWatchlist = useMemo(
    () => data?.player?.isWatchedByCurrentUser,
    [data?.player?.isWatchedByCurrentUser]
  );

  const handleAddPlayer = useCallback(() => setShowActionSheet(true), []);
  const handleCloseActionSheet = useCallback(
    () => setShowActionSheet(false),
    []
  );
  const handleRemovePlayer = useCallback(() => {
    return removePlayerFromRecommendations({
      variables: { playerId: data?.player?.id },
      refetchQueries: ["RecommendationsListPlayerRecommendations"],
      optimisticResponse: {
        __typename: "Mutation",
        removePlayerFromRecommendations: {
          __typename: "Player",
          ...data?.player,
        },
      },
      update: (cache, { data: updateData, errors }) => {
        const playerId = updateData?.removePlayerFromRecommendations?.id;
        if (!!data?.id && !!playerId && !errors) {
          cache.modify({
            id: cache.identify({ __typename: "Query", id: "ROOT_QUERY" }),
            fields: {
              [`listPlayerRecommendations({"recommendationType":"${recommendationsCategorySelected}"})`](
                existingData: readonly { __ref: string }[]
              ) {
                return existingData.filter(
                  (item: { __ref: string }) =>
                    item.__ref !== `PlayerRecommendationResult:${data.id}`
                );
              },
            },
          });
        }
      },
      onCompleted: () => {
        recordRemovePlayerFromRecommendationsGoal();
        toast.show({
          description: "Player is removed from recommendations",
          action: "success",
        });
      },
    });
  }, [
    data.id,
    data.player,
    recommendationsCategorySelected,
    recordRemovePlayerFromRecommendationsGoal,
    removePlayerFromRecommendations,
    toast,
  ]);

  const handleConfirmDeletePlayer = useCallback(() => {
    if (Platform.OS === "web") {
      const doDelete = window.confirm(
        `Are you sure you want to remove ${playerName}?`
      );
      return doDelete ? handleRemovePlayer() : null;
    }

    return new Promise((resolve, reject) =>
      Alert.alert(
        `Remove ${playerName}`,
        `Are you sure you want to remove ${playerName}?`,
        [
          { text: "Cancel", style: "cancel", onPress: () => resolve(null) },
          {
            text: "Yes",
            onPress: () => handleRemovePlayer().then(resolve).catch(reject),
            style: "destructive",
          },
        ]
      )
    );
  }, [handleRemovePlayer, playerName]);

  const handleOpenPlayerDetail = useCallback(() => {
    clearBreadcrumbs();
    pushBreadcrumb({ label: "Home", path: "/" });
    pushBreadcrumb({ label: playerName || "Player", path: playerUrl });
    router.push(playerUrl as Href);
  }, [playerUrl, router, clearBreadcrumbs, pushBreadcrumb, playerName]);

  const handlePressPlayer = useCallback(() => {
    playerDetailVar?.({
      type: "roster",
      player: data.player,
      infoRecommendation: {
        id: data.id,
        type: recommendationsCategorySelected,
      },
    });
    clearBreadcrumbs();
    pushBreadcrumb({ label: "Home", path: "/" });
    pushBreadcrumb({ label: playerName || "Player", path: playerUrl });
    router.push(playerUrl as Href);
  }, [
    data.id,
    data.player,
    recommendationsCategorySelected,
    router,
    playerUrl,
    clearBreadcrumbs,
    pushBreadcrumb,
    playerName,
  ]);

  return (
    <RecommendationPlayerView
      player={data?.player}
      showActionsheet={showActionSheet}
      isPlayerInAnyWatchlist={!!isPlayerInAnyWatchlist}
      isDeleting={isDeleting || loading}
      promptRef={promptRef}
      playerName={playerName}
      rankChange={rankChange}
      positionAndTeamName={positionAndTeamName}
      isSmallScreen={isSmallScreen}
      onAdd={handleAddPlayer}
      onAddWatchlistPress={handleAddWatchlistPress}
      onCloseActionSheet={handleCloseActionSheet}
      onOpenPlayerDetail={handleOpenPlayerDetail}
      onPressPlayer={handlePressPlayer}
      onRemovePlayer={handleConfirmDeletePlayer}
      isDark={isDark}
    />
  );
};
export default RecommendationPlayerContainer;
